import { localizedUrl } from "@/lib/utils/site-url";
import { activeDictLang } from "@/lib/i18n-dict";

/**
 * BreadcrumbList and ItemList structured data.
 *
 * docs/project/content-seo-plan.md P1.2 and P1.3, neither of which had been started.
 * Four route types render a visible breadcrumb trail — product category,
 * product detail, blog article, store category — and none of them told Google
 * what the trail said, so the hierarchy the site displays to a reader was
 * invisible to a crawler. Breadcrumbs are also what Google renders in place of
 * the raw URL in a result, so an unannotated page shows its path instead of
 * its position.
 *
 * Both builders take their URLs through localizedUrl(), so a Turkish page
 * describes Turkish URLs. The alternative — every locale emitting the English
 * hierarchy — is the bug the @id comments in blog.$category.$slug.tsx record
 * being fixed once already.
 */

export interface Crumb {
  name: string;
  /** Locale-free path, e.g. "/products/seafood". Omit for the current page. */
  path?: string;
}

/**
 * A BreadcrumbList, ready to hand to head().scripts.
 *
 * The last crumb intentionally carries no `item`. Google's guidance is that
 * the final element is the current page and may omit its URL; supplying one
 * that equals the canonical adds nothing and gives a second place for the two
 * to disagree.
 */
export function breadcrumbSchema(crumbs: Crumb[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      ...(crumb.path ? { item: localizedUrl(crumb.path) } : {}),
    })),
  };
}

export interface ListEntry {
  name: string;
  /** Locale-free path to the thing being listed. */
  path: string;
}

/**
 * An ItemList for a listing page — a product category, a blog category.
 *
 * `ItemListOrderAscending` is deliberately not claimed: these grids render in
 * source order, which is neither alphabetical nor by any published rank, and
 * asserting an order that the page does not follow is worse than leaving it
 * unspecified.
 */
export function itemListSchema(name: string, entries: ListEntry[]) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    inLanguage: activeDictLang() ?? "en",
    numberOfItems: entries.length,
    itemListElement: entries.map((entry, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: entry.name,
      url: localizedUrl(entry.path),
    })),
  };
}

/** head().scripts entry for a JSON-LD object, with a stable id. */
export function jsonLdScript(id: string, data: unknown) {
  return {
    type: "application/ld+json",
    // A stable id so a client-side navigation replaces the tag rather than
    // appending a second one, which is the same reason faq-schema.ts uses
    // "ld-faq". Two BreadcrumbLists on one document is a validation error.
    id,
    children: JSON.stringify(data),
  };
}
